import {Autocomplete, Box, createFilterOptions, Grid, TextField, TextFieldVariants, Typography} from "@mui/material";
import {useEmployeeStore} from "../../api/EmployeeStore";
import {Employee, EmployeeRef} from "../../api/Model";
import {TextFieldProps} from "@mui/material/TextField/TextField";
import {FilterOptionsState} from "@mui/material/useAutocomplete/useAutocomplete";
import * as React from "react";
import {useCallback} from "react";
import {autocompleteChangeEvent, SetState} from "../../util/FormHandlers";
import {AutocompleteRenderInputParams} from "@mui/material/Autocomplete/Autocomplete";
import {SiteChip} from "../site-chip/SiteChip";
import {idFn} from "../../util/FunctionUtil";
import {useRemoteValue} from "../../util/RemoteValue";
import {Tag} from "../tag/Tag";
import {EmployeeKeyHistory, useEmployeeKeyHistoryStore} from "../../api/EmployeeKeyHistoryStore";
import {emptyEmployee} from "../../api/Empty";

function formatHasKeys(
    employee: EmployeeRef,
    employeeKeyHistory: EmployeeKeyHistory,
): string {
    const activeKeys = employeeKeyHistory.active(employee)
    switch (activeKeys.length) {
        case 0:
            return "has no keys";
        case 1:
            return `has key ${activeKeys[0].key.keyNumber} from ${activeKeys[0].historyRecord.vault.name}`;
        default:
            return `has keys ${activeKeys.map(key => key.key.keyNumber).join(", ")}`;
    }
}

const typeToSeeMoreMessage: Employee = {
    ...emptyEmployee,
    fullName: 'Start typing name to see other employees...',
};

const optionLabel = (option: Employee) => option.fullName;
const optionDisabled = (option: Employee) => option === typeToSeeMoreMessage;

const OPTION_LIMIT = 100;
const basicFilterOptions = createFilterOptions<Employee>({
    limit: OPTION_LIMIT,
})

function filterOptions(options: Employee[], state: FilterOptionsState<Employee>): Employee[] {
    const employees = basicFilterOptions(options, state);
    if (employees.length === OPTION_LIMIT) {
        employees.push(typeToSeeMoreMessage);
    }
    return employees;
}

export function EmployeeSelect({employee, setEmployee, clearable = false, ...props}: {
    employee?: Employee,
    setEmployee: SetState<Employee | undefined>,
    clearable?: boolean,
} & {
    /**
     * The variant to use.
     * @default 'outlined'
     */
    variant?: TextFieldVariants;
} & Omit<TextFieldProps, 'variant'>) {
    const employeeStore = useEmployeeStore();
    const employeeKeyHistoryStore = useEmployeeKeyHistoryStore();
    const employees = useRemoteValue(employeeStore.remoteValue, []);
    const employeeKeyHistory = useRemoteValue(employeeKeyHistoryStore.remoteValue, new EmployeeKeyHistory([]));

    // eslint-disable-next-line
    const onEmployeeSelect = useCallback(
        autocompleteChangeEvent<Employee | null>((employee) => {
            setEmployee(employee && employee !== emptyEmployee ? employee : undefined);
        }), [setEmployee]
    );

    const renderInput = useCallback((params: AutocompleteRenderInputParams) => (
        <TextField {...params} {...props} />
    ), [props]);

    const renderOption = useCallback((optionProps: React.HTMLAttributes<HTMLLIElement> & { key: any }, employee: Employee) => {
        return employee !== typeToSeeMoreMessage
            ? (
                <Grid {...optionProps} container spacing={2} alignItems="center" component="li"
                      key={optionProps.key}>
                    <Grid>
                        <Typography
                            component="span"
                            sx={{opacity: theme => employee.deactivated ? theme.palette.action.disabledOpacity : undefined,}}
                        >{employee.fullName}</Typography>
                    </Grid>
                    {employee.deactivated ? (
                        <Tag color="disabled" typography="caption">inactive</Tag>
                    ) : (
                        <SiteChip employee={employee}/>
                    )}
                    <Grid size="grow" textAlign="right"
                          color="text.secondary">{formatHasKeys(employee, employeeKeyHistory)}</Grid>
                </Grid>
            )
            : (
                <Box {...optionProps} component="li" key={optionProps.key}>
                    <Typography variant="body2">{employee.fullName}</Typography>
                </Box>
            );
    }, [employeeKeyHistory]);

    return (
        <Autocomplete
            options={employees}
            filterOptions={filterOptions}
            getOptionKey={idFn}
            getOptionLabel={optionLabel}
            renderInput={renderInput}
            renderOption={renderOption}
            getOptionDisabled={optionDisabled}
            value={employee || emptyEmployee}
            onChange={onEmployeeSelect}
            disableClearable={!clearable}
        />
    );
}